import { AxiosResponse } from 'axios'
import { catchError, Observable, of } from 'rxjs'
import { map } from 'rxjs/operators'

import { environment } from '@resume-portal/environment'
import { ITrackProperties } from '@resume-portal/features/resume-builder/models'
import { axios } from '@resume-portal/lib'
import { userOrganizationStore } from '@resume-portal/stores'

import {
  IActivateUserOrganization,
  IAddUserOrganizationTeamRequestDto,
  IAddUserOrganizationTeamUserRequestDto,
  IApproveUserOrganizationJoinRequestDto,
  IBillingPeriodInfo,
  IBillingPortalSession,
  ICancelServicePlanRequestDto,
  ICheckoutSession,
  ICreateCheckoutSessionRequestDto,
  ICreateUserOrganizationRequestDto,
  IDiscoveredOrganization,
  IEditUserAccessTypeRequestDto,
  IEditUserOrganizationRequestDto,
  IOrganizationTeam,
  IRemainingUsage,
  IResendInvitationRequestDto,
  IUserOrganization,
  IVerifyCheckoutSessionRequestDto,
} from '../models'

class UserOrganizationService {
  private readonly baseUrl = `${environment.AUTH_API_URL}/UserOrganization`

  getPrimaryUserOrganization(): Observable<IUserOrganization | null> {
    return axios.get<IUserOrganization>(this.baseUrl).pipe(
      map(({ data }) => {
        userOrganizationStore.dispatch({ userOrganization: data })
        return data
      }),
      catchError(() => of(null))
    )
  }

  getAvailableUserOrganizations(): Observable<IDiscoveredOrganization[]> {
    return axios
      .get<IDiscoveredOrganization[]>(`${this.baseUrl}/discover`)
      .pipe(map(({ data }) => data))
  }

  createUserOrganization(
    payload: ICreateUserOrganizationRequestDto
  ): Observable<IUserOrganization> {
    return axios.post<IUserOrganization>(this.baseUrl, payload).pipe(
      map(({ data }) => {
        userOrganizationStore.dispatch({
          userOrganization: data,
        })
        return data
      })
    )
  }

  activateUserOrganization(
    payload: IActivateUserOrganization
  ): Observable<IUserOrganization> {
    return axios
      .patch<IUserOrganization>(`${this.baseUrl}/${payload.id}`, payload)
      .pipe(map(({ data }) => data))
  }

  joinOrganization(
    orgId: string,
    userName?: string
  ): Observable<IUserOrganization> {
    return axios
      .post(
        `${this.baseUrl}/${orgId}/join?${
          userName ? new URLSearchParams({ userName }) : null
        }`
      )
      .pipe(map(({ data }) => data))
  }

  requestJoinOrganization(orgId: string, userName?: string): Observable<any> {
    return axios
      .post(
        `${this.baseUrl}/${orgId}/request-join?${
          userName ? new URLSearchParams({ userName }) : null
        }`,
        {
          Domain: window.location.origin,
        }
      )
      .pipe(map(({ data }) => data))
  }

  checkIfPendingOrganization(): Observable<boolean> {
    return axios.get<boolean>(`${this.baseUrl}/check-pending-user`).pipe(
      map(({ data }) => {
        userOrganizationStore.dispatch({ isPendingJoin: data })
        return data
      })
    )
  }

  editUserOrganization(
    id: string,
    payload: IEditUserOrganizationRequestDto
  ): Observable<IUserOrganization> {
    return axios
      .patch<IUserOrganization>(`${this.baseUrl}/${id}`, payload)
      .pipe(
        map(({ data }) => {
          userOrganizationStore.dispatch({ userOrganization: data })
          return data
        })
      )
  }

  requestPlanUpgrade(orgId: string): Observable<any> {
    return axios
      .post(`${this.baseUrl}/${orgId}/request-service-plan-upgrade`)
      .pipe(map(({ data }) => data))
  }

  requestCustomTemplate(orgId: string): Observable<any> {
    return axios
      .post(`${this.baseUrl}/${orgId}/request-custom-template`)
      .pipe(map(({ data }) => data))
  }

  trackUsage(properties: ITrackProperties): Observable<AxiosResponse> {
    return axios.post(
      `${environment.GORILLA_RESUME_API_URL}/UsageTrack`,
      properties
    )
  }

  getRemainingUsage(orgId: string): Observable<IRemainingUsage> {
    const url = `${environment.GORILLA_RESUME_API_URL}/UsageTrack/organization/${orgId}/remaining-usage`
    return axios.get<IRemainingUsage>(url).pipe(
      map(({ data }) => {
        userOrganizationStore.dispatch({ remainingUsage: data })
        return data
      })
    )
  }

  addTeam(
    id: string,
    payload: IAddUserOrganizationTeamRequestDto
  ): Observable<IUserOrganization> {
    return axios
      .post<IUserOrganization>(`${this.baseUrl}/${id}/team`, payload)
      .pipe(
        map(({ data }) => {
          userOrganizationStore.dispatch({ userOrganization: data })
          return data
        })
      )
  }

  addTeamUser(
    id: string,
    teamId: string,
    payload: IAddUserOrganizationTeamUserRequestDto
  ): Observable<IUserOrganization> {
    return axios
      .post<IUserOrganization>(
        `${this.baseUrl}/${id}/team/${teamId}/user`,
        payload
      )
      .pipe(
        map(({ data }) => {
          userOrganizationStore.dispatch({ userOrganization: data })
          return data
        })
      )
  }

  approveUserJoinRequest(
    id: string,
    payload: IApproveUserOrganizationJoinRequestDto
  ): Observable<IUserOrganization> {
    return axios
      .post<IUserOrganization>(`${this.baseUrl}/${id}/approve-join`, payload)
      .pipe(
        map(({ data }) => {
          userOrganizationStore.dispatch({ userOrganization: data })
          return data
        })
      )
  }

  editUserOrgAccessType(
    id: string,
    userId: string,
    payload: IEditUserAccessTypeRequestDto
  ): Observable<IUserOrganization> {
    return axios
      .patch<IUserOrganization>(`${this.baseUrl}/${id}/user/${userId}`, payload)
      .pipe(
        map(({ data }) => {
          userOrganizationStore.dispatch({ userOrganization: data })
          return data
        })
      )
  }

  editUserTeamAccessType(
    id: string,
    teamId: string,
    userId: string,
    payload: IEditUserAccessTypeRequestDto
  ): Observable<IUserOrganization> {
    return axios
      .patch<IUserOrganization>(
        `${this.baseUrl}/${id}/team/${teamId}/user/${userId}`,
        payload
      )
      .pipe(
        map(({ data }) => {
          userOrganizationStore.dispatch({ userOrganization: data })
          return data
        })
      )
  }

  updateTeam(
    id: string,
    teamId: string,
    team: IOrganizationTeam
  ): Observable<IUserOrganization> {
    return axios
      .put<IUserOrganization>(`${this.baseUrl}/${id}/team/${teamId}`, team)
      .pipe(
        map(({ data }) => {
          userOrganizationStore.dispatch({ userOrganization: data })
          return data
        })
      )
  }

  deleteTeam(id: string, teamId: string): Observable<IUserOrganization> {
    return axios
      .delete<IUserOrganization>(`${this.baseUrl}/${id}/team/${teamId}`)
      .pipe(
        map(({ data }) => {
          userOrganizationStore.dispatch({ userOrganization: data })
          return data
        })
      )
  }

  checkAndActivateUser(): Observable<IUserOrganization | null> {
    return axios.post(`${this.baseUrl}/activate-user`).pipe(
      map(({ data }) => {
        userOrganizationStore.dispatch({ userOrganization: data })
        return data
      }),
      catchError(() => of(null))
    )
  }

  deleteOrganizationUser(
    id: string,
    userId: string
  ): Observable<IUserOrganization> {
    return axios
      .delete<IUserOrganization>(`${this.baseUrl}/${id}/user/${userId}`)
      .pipe(
        map(({ data }) => {
          userOrganizationStore.dispatch({ userOrganization: data })
          return data
        })
      )
  }

  deleteTeamUser(
    id: string,
    teamId: string,
    userId: string
  ): Observable<IUserOrganization> {
    return axios
      .delete<IUserOrganization>(
        `${this.baseUrl}/${id}/team/${teamId}/user/${userId}`
      )
      .pipe(
        map(({ data }) => {
          userOrganizationStore.dispatch({ userOrganization: data })
          return data
        })
      )
  }

  resendInvitation(
    id: string,
    payload: IResendInvitationRequestDto
  ): Observable<IUserOrganization> {
    return axios
      .post<IUserOrganization>(
        `${this.baseUrl}/${id}/resend-invitation`,
        payload
      )
      .pipe(
        map(({ data }) => {
          userOrganizationStore.dispatch({ userOrganization: data })
          return data
        })
      )
  }

  createCheckoutSession(
    id: string,
    payload: ICreateCheckoutSessionRequestDto
  ): Observable<ICheckoutSession> {
    return axios
      .post(`${this.baseUrl}/${id}/create-checkout-session`, payload)
      .pipe(map(({ data }) => data))
  }

  createBillingPortalSession(id: string): Observable<IBillingPortalSession> {
    return axios
      .post(`${this.baseUrl}/${id}/create-billing-portal-session`)
      .pipe(map(({ data }) => data))
  }

  updateServicePlan(
    id: string,
    servicePlanId: string
  ): Observable<IUserOrganization> {
    return axios
      .patch<IUserOrganization>(
        `${this.baseUrl}/${id}/service-plan/${servicePlanId}`
      )
      .pipe(
        map(({ data }) => {
          userOrganizationStore.dispatch({ userOrganization: data })
          return data
        })
      )
  }

  cancelServicePlan(
    id: string,
    payload: ICancelServicePlanRequestDto
  ): Observable<IUserOrganization> {
    return axios
      .patch<IUserOrganization>(
        `${this.baseUrl}/${id}/cancel-service-plan`,
        payload
      )
      .pipe(
        map(({ data }) => {
          userOrganizationStore.dispatch({ userOrganization: data })
          return data
        })
      )
  }

  verifyCheckoutSession(
    id: string,
    payload: IVerifyCheckoutSessionRequestDto
  ): Observable<void> {
    return axios
      .patch<IUserOrganization>(
        `${this.baseUrl}/${id}/verify-checkout-session`,
        payload
      )
      .pipe(map(() => {}))
  }

  getBillingPeriodInfo(id: string): Observable<IBillingPeriodInfo> {
    return axios
      .get<IBillingPeriodInfo>(`${this.baseUrl}/${id}/billing-period`)
      .pipe(
        map(({ data }) => {
          return data
        })
      )
  }

  downloadLogo(
    organizationId: string,
    logoId: string
  ): Observable<AxiosResponse<ArrayBuffer>> {
    return axios
      .get<ArrayBuffer>(`${this.baseUrl}/${organizationId}/logo/${logoId}`, {
        responseType: 'blob',
      })
      .pipe(map((data) => data))
  }
}

export const userOrganizationService = new UserOrganizationService()
